import React, { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { fieldDomId } from '../../services/assessmentSchema';

// One input per field type in the form definition. Every input reports a
// value straight up through onChange(key, value); null clears an answer.
// Choice-style inputs clear when the selected option is tapped again, so a
// mis-tap is always undoable without a separate "clear" control.

const pill = (active, disabled) => [
  'min-h-[2.5rem] rounded-lg border px-3 py-2 text-sm text-left transition-colors',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
  active
    ? 'border-blue-600 bg-blue-600 text-white'
    : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400',
  disabled ? 'opacity-60 cursor-not-allowed' : '',
].join(' ');

// Browsers offer previously typed answers (and password managers offer
// identities) on any text box; on a clinical form that is noise at best and a
// leak of another student's answer at worst.
export const NO_AUTOFILL = {
  autoComplete: 'off',
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-form-type': 'other',
};

const inputClass = 'block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 '
  + 'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50';

function AutoTextarea({ id, value, onChange, disabled, minRows = 2, placeholder }) {
  const ref = useRef(null);
  const fit = () => {
    const el = ref.current;
    // A hidden textarea measures 0; sizing it then would collapse it.
    if (!el || !el.offsetParent) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  };
  useLayoutEffect(fit, [value]);
  // Re-fit when it becomes visible or its width changes (e.g. switching back
  // from the memo Upload tab, rotating a phone).
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let width = el.offsetWidth;
    const observer = new ResizeObserver(() => {
      if (el.offsetWidth !== width) {
        width = el.offsetWidth;
        fit();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <textarea
      {...NO_AUTOFILL}
      id={id}
      ref={ref}
      rows={minRows}
      className={`${inputClass} resize-none overflow-hidden`}
      value={value ?? ''}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
    />
  );
}

function NumberInput({ id, field, value, onChange, disabled }) {
  // Raw text is kept locally so a half-typed or out-of-range number is never
  // sent; only a valid whole number in range becomes the answer.
  const [raw, setRaw] = useState(value ?? '');
  useEffect(() => { setRaw((r) => (String(r) === String(value ?? '') ? r : (value ?? ''))); }, [value]);
  const n = raw === '' ? null : Number(raw);
  const valid = n === null || (Number.isInteger(n) && n >= field.min && n <= field.max);
  return (
    <div>
      <input
        {...NO_AUTOFILL}
        id={id}
        type="number"
        inputMode="numeric"
        min={field.min}
        max={field.max}
        className={`${inputClass} max-w-[10rem] ${valid ? '' : 'border-red-400'}`}
        value={raw}
        disabled={disabled}
        onChange={(e) => {
          setRaw(e.target.value);
          const v = e.target.value === '' ? null : Number(e.target.value);
          if (v === null || (Number.isInteger(v) && v >= field.min && v <= field.max)) onChange(v);
        }}
      />
      {!valid && (
        <p className="mt-1 text-xs text-red-600">Enter a whole number from {field.min} to {field.max}.</p>
      )}
    </div>
  );
}

function Control({ field, value, onChange, disabled, id }) {
  const set = (v) => onChange(field.key, v);
  switch (field.type) {
    case 'text':
      return (
        <>
          <input
            {...NO_AUTOFILL}
            id={id}
            type="text"
            className={inputClass}
            value={value ?? ''}
            disabled={disabled}
            placeholder={field.placeholder}
            list={field.suggestions ? `${id}-list` : undefined}
            onChange={(e) => set(e.target.value === '' ? null : e.target.value)}
          />
          {field.suggestions && (
            <datalist id={`${id}-list`}>
              {field.suggestions.map((s) => <option key={s} value={s} />)}
            </datalist>
          )}
        </>
      );
    case 'textarea':
      return <AutoTextarea id={id} value={value} onChange={set} disabled={disabled} placeholder={field.placeholder} />;
    case 'number':
      return <NumberInput id={id} field={field} value={value} onChange={set} disabled={disabled} />;
    case 'date':
      return (
        <input {...NO_AUTOFILL} id={id} type="date" className={`${inputClass} max-w-[12rem]`} value={value ?? ''}
          disabled={disabled} onChange={(e) => set(e.target.value || null)} />
      );
    case 'select':
      return (
        <select id={id} className={`${inputClass} max-w-xs`} value={value ?? ''} disabled={disabled}
          onChange={(e) => {
            const opt = field.options.find((o) => String(o.value) === e.target.value);
            set(opt ? opt.value : null);
          }}>
          <option value="">Select…</option>
          {field.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    case 'yesno':
      return (
        <div id={id} role="radiogroup" className="flex gap-2">
          {[[true, 'Yes'], [false, 'No']].map(([v, label]) => (
            <button key={label} type="button" role="radio" aria-checked={value === v} disabled={disabled}
              className={`${pill(value === v, disabled)} w-20 text-center`}
              onClick={() => set(value === v ? null : v)}>
              {label}
            </button>
          ))}
        </div>
      );
    case 'check':
      return (
        <label className="inline-flex items-center gap-3 text-sm text-slate-700 min-h-[2.5rem] cursor-pointer">
          <input id={id} type="checkbox" className="h-5 w-5 rounded border-slate-300 text-blue-600"
            checked={value === true} disabled={disabled} onChange={(e) => set(e.target.checked ? true : null)} />
          {field.label}
        </label>
      );
    case 'choice':
      return (
        <div id={id} role="radiogroup" className="flex flex-wrap gap-2">
          {field.options.map((o) => (
            <button key={o.value} type="button" role="radio" aria-checked={value === o.value} disabled={disabled}
              className={pill(value === o.value, disabled)}
              onClick={() => set(value === o.value ? null : o.value)}>
              {o.label}
            </button>
          ))}
        </div>
      );
    case 'multi': {
      const selected = Array.isArray(value) ? value : [];
      const toggle = (opt) => {
        let next;
        if (selected.includes(opt.value)) {
          next = selected.filter((v) => v !== opt.value);
        } else if (opt.exclusive) {
          next = [opt.value];
        } else {
          const exclusive = new Set(field.options.filter((o) => o.exclusive).map((o) => o.value));
          next = [...selected.filter((v) => !exclusive.has(v)), opt.value];
        }
        set(next.length ? next : null);
      };
      return (
        <div id={id} className="flex flex-wrap gap-2">
          {field.options.map((o) => (
            <button key={o.value} type="button" role="checkbox" aria-checked={selected.includes(o.value)}
              disabled={disabled} className={pill(selected.includes(o.value), disabled)} onClick={() => toggle(o)}>
              {o.label}
            </button>
          ))}
        </div>
      );
    }
    case 'scale': {
      const steps = [];
      for (let n = field.min; n <= field.max; n += 1) steps.push(n);
      return (
        <div id={id}>
          <div role="radiogroup" className="flex gap-2">
            {steps.map((n) => (
              <button key={n} type="button" role="radio" aria-checked={value === n} disabled={disabled}
                className={`${pill(value === n, disabled)} w-11 text-center`}
                onClick={() => set(value === n ? null : n)}>
                {n}
              </button>
            ))}
          </div>
          <div className="mt-1 flex justify-between text-xs text-slate-500 max-w-[16.5rem]">
            <span>{field.lowLabel}</span>
            <span className="text-right">{field.highLabel}</span>
          </div>
        </div>
      );
    }
    default:
      return <p className="text-sm text-red-600">Unsupported field type: {field.type}</p>;
  }
}

function FormField({ field, value, onChange, disabled, visible, isMissing }) {
  const id = fieldDomId(field.key);
  const hasValue = !(value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length));

  if (!visible) {
    // Hidden follow-ups are never cleared; say so when one still holds an answer.
    return hasValue ? (
      <p className="text-xs text-slate-500 italic">
        Answer kept for &ldquo;{field.label}&rdquo; (hidden by an earlier answer).
      </p>
    ) : null;
  }

  return (
    <div className={`scroll-mt-24 ${isMissing ? 'rounded-lg ring-2 ring-amber-300 ring-offset-4' : ''}`}>
      {field.type !== 'check' && (
        <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-slate-800">
          {field.label}
          {field.required && <span className="ml-0.5 text-red-500" aria-label="required">*</span>}
        </label>
      )}
      <Control field={field} value={value} onChange={onChange} disabled={disabled} id={id} />
    </div>
  );
}

export default memo(FormField);
